import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import api from '../../services/api';

/**
 * Modal component for configuring AI response delay settings
 * Extracted from ConversationView.js to reduce component complexity
 */
const DelaySettingsModal = ({ 
  open, 
  onClose, 
  conversationId, 
  token, 
  clientName,
  onSuccess 
}) => {
  const [delaySettingsData, setDelaySettingsData] = useState({
    overridePlatformDefaults: false,
    thinkingDelayMs: 2000,
    typingDelayMs: 3000,
    enableRandomization: false,
    thinkingDelayMaxMs: 4000,
    typingDelayMaxMs: 5000
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setDelaySettingsData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value, 10)
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!conversationId || !token) {
      setError('Missing conversation ID or token');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const updatedConversationData = await api.updateConversationAiDelaySettings(
        conversationId,
        delaySettingsData,
        token
      );
      
      if (onSuccess) {
        onSuccess(updatedConversationData);
      }
      
      onClose();
    } catch (err) {
      console.error("Error saving AI delay settings:", err);
      setError(err.response?.data?.msg || err.message || "Failed to save AI delay settings.");
    } finally {
      setLoading(false);
    }
  }, [conversationId, token, delaySettingsData, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    setError('');
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        AI Response Delay Settings for {clientName || 'this client'}
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <FormControlLabel
          control={
            <Switch
              checked={delaySettingsData.overridePlatformDefaults}
              onChange={handleInputChange}
              name="overridePlatformDefaults"
            />
          }
          label="Override Platform Default Delays for this Conversation"
          sx={{ mb: 1, display: 'block' }}
        />
        
        {delaySettingsData.overridePlatformDefaults && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Thinking Delay (ms)"
                name="thinkingDelayMs"
                value={delaySettingsData.thinkingDelayMs}
                onChange={handleInputChange}
                helperText="AI 'thinks' before 'typing'"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Typing Delay (ms)"
                name="typingDelayMs"
                value={delaySettingsData.typingDelayMs}
                onChange={handleInputChange}
                helperText="Duration of 'typing' indicator"
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={delaySettingsData.enableRandomization}
                    onChange={handleInputChange}
                    name="enableRandomization"
                  />
                }
                label="Enable Delay Randomization"
                sx={{ display: 'block' }}
              />
            </Grid>
            
            {delaySettingsData.enableRandomization && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Thinking Delay (ms)"
                    name="thinkingDelayMaxMs"
                    value={delaySettingsData.thinkingDelayMaxMs}
                    onChange={handleInputChange}
                    helperText="Upper bound for thinking delay"
                    InputProps={{ 
                      inputProps: { min: delaySettingsData.thinkingDelayMs } 
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Typing Delay (ms)"
                    name="typingDelayMaxMs"
                    value={delaySettingsData.typingDelayMaxMs}
                    onChange={handleInputChange}
                    helperText="Upper bound for typing delay"
                    InputProps={{ 
                      inputProps: { min: delaySettingsData.typingDelayMs } 
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelaySettingsModal;